import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { Service, ServiceLike } from './service';
import { createSwitchingService } from './switchingService';

export function createProjectingService<T, S extends Service>(
  initialValue: T,
  value$: Observable<T>,
  getService: (value: T) => S,
  getSwitchEffects?: (service$: Observable<S>) => Observable<any>,
  ...dependencies: ServiceLike[]
): S {
  // Eagerly obtain the service for the initial value and then each value emitted moving forward. This subscription
  // should not leak as long as the provided Observable is finite or garbage collectable (such a Subject).
  let currentService: S;
  value$
    .pipe(
      // Start with the initial value.
      startWith(initialValue),
      // De-dupe, because getService() might be expensive.
      distinctUntilChanged(),
      map(getService),
    )
    .subscribe(service => {
      currentService = service;
    });

  // @ts-ignore
  if (!currentService) {
    throw new Error('Assertion failed: expected initial service to be available.');
  }

  // Map the original value stream to the current service, so that subscriptions are passed upstream and subscription
  // order is maintained.
  const service$ = value$.pipe(map(() => currentService));

  return createSwitchingService(currentService, service$, getSwitchEffects, ...dependencies);
}
